import type { CompanyResponse, PublicBrandDto, ResolvedBrand } from '@saas/api-client';
import type { CSSProperties } from 'react';

import { contrastRatio, hexToOklch, type Oklch, oklchString } from './oklch';

// The platform's own brand colour, the Cobalt blue base the design system binds to `base/primary`.
// A null-resolved ("no brand") ResolvedBrand renders this scale. This is not a sentinel: a tenant
// that picks this exact hex still resolves as branded because resolveBrand sets isBranded, and this
// module trusts that flag, never a hex comparison.
export const PLATFORM_THEME_COLOR = '#2563EB';

// Foreground candidates measured against the brand for the WCAG-contrast pick.
const DARK_FOREGROUND_HEX = '#101317';
const LIGHT_FOREGROUND_HEX = '#FFFFFF';
const MIN_CONTRAST = 4.5;

// The oklch lightness applied to each foreground candidate so the emitted token keeps the
// brand hue while landing on the readable end of the scale.
const DARK_FOREGROUND: Oklch = { l: 21, c: 0.02, h: 0 };
const LIGHT_FOREGROUND: Oklch = { l: 98, c: 0.01, h: 0 };

export type BrandThemeVars = CSSProperties & Record<`--${string}`, string>;

interface InteractionScale {
	base: string;
	hover: string;
	active: string;
	disabled: string;
	subtle: string;
}

function withLightness(color: Oklch, l: number): string {
	return oklchString({ ...color, l: parseFloat(l.toFixed(2)) });
}

// Derives the hover/active/disabled/subtle scale in OKLCH by nudging lightness and chroma off
// the base, so every step keeps the brand hue instead of blending toward grey.
function interactionScale(color: Oklch): InteractionScale {
	const base = oklchString(color);
	const darker = Math.max(color.l - 6, 0);
	const darkest = Math.max(color.l - 12, 0);
	return {
		base,
		hover: withLightness(color, darker),
		active: withLightness(color, darkest),
		disabled: `oklch(${base.slice(6, -1)} / 0.45)`,
		subtle: `oklch(${withLightness(color, Math.min(color.l + 8, 97)).slice(6, -1)} / 0.14)`,
	};
}

// Picks the readable text-on-brand colour by measured WCAG contrast (>=4.5:1), keeping the
// brand hue. Not a lightness threshold: mid-tone brands that fail both candidates fall back to
// whichever side reads better, so the foreground is never worse than the best available option.
function foregroundFor(themeColor: string): string {
	const darkContrast = contrastRatio(themeColor, DARK_FOREGROUND_HEX);
	const lightContrast = contrastRatio(themeColor, LIGHT_FOREGROUND_HEX);
	const hue = hexToOklch(themeColor).h;

	if (darkContrast >= MIN_CONTRAST && darkContrast >= lightContrast) return oklchString({ ...DARK_FOREGROUND, h: hue });
	if (lightContrast >= MIN_CONTRAST) return oklchString({ ...LIGHT_FOREGROUND, h: hue });
	return darkContrast >= lightContrast
		? oklchString({ ...DARK_FOREGROUND, h: hue })
		: oklchString({ ...LIGHT_FOREGROUND, h: hue });
}

function themeVarsFor(themeColor: string): BrandThemeVars {
	const color = hexToOklch(themeColor);
	const scale = interactionScale(color);
	const foreground = foregroundFor(themeColor);
	return {
		'--brand-color': scale.base,
		'--brand-color-foreground': foreground,
		'--brand-color-hover': scale.hover,
		'--brand-color-active': scale.active,
		'--brand-color-disabled': scale.disabled,
		'--brand-color-subtle': scale.subtle,
	};
}

// The one module that turns a resolved Brand into scoped CSS custom properties. Spread the
// result onto a wrapper carrying data-brand-theme so a portal/offer/meeting subtree themes
// independently of the app chrome. A null-resolved brand (isBranded === false) yields the
// platform scale, so no render site ever picks a colour.
export function resolveBrandTheme(brand: ResolvedBrand): BrandThemeVars {
	const themeColor = brand.isBranded ? brand.themeColor : PLATFORM_THEME_COLOR;
	return themeVarsFor(themeColor);
}

// The platform scale, for a surface that renders before any brand resolves.
export function platformBrandTheme(): BrandThemeVars {
	return themeVarsFor(PLATFORM_THEME_COLOR);
}

export type BrandIdentityFields = {
	themeColor: string | null;
	logoUrl: string | null;
	customAppName: string | null;
};

// A workspace is branded when it carries any presentable identity of its own. Colour, logo, or
// custom app name each count; a workspace with none resolves to the platform brand.
export function companyIsBranded(whiteLabeling: BrandIdentityFields | null): boolean {
	if (!whiteLabeling) return false;
	return Boolean(whiteLabeling.themeColor || whiteLabeling.logoUrl || whiteLabeling.customAppName);
}

const PLATFORM_DISPLAY_NAME = 'Vadio';

type ResolvedBrandParts = {
	companyId: string | null;
	displayName: string;
	logoUrl: string | null;
	faviconUrl: string | null;
	themeColor: string | null;
	isBranded: boolean;
};

// The single constructor every frontend brand mapper funnels through. It applies the platform-colour
// null-resolution once (an unbranded brand, or one with no colour of its own, rides the platform
// hue) and fills the constant fields a chrome-side resolve never carries (support contacts,
// endorsement). Render sites read `isBranded`, never the raw fields, so this is the only place the
// branded decision meets the platform fallback.
export function makeResolvedBrand(parts: ResolvedBrandParts): ResolvedBrand {
	return {
		companyId: parts.companyId,
		displayName: parts.displayName,
		endorsement: null,
		faviconUrl: parts.faviconUrl,
		isBranded: parts.isBranded,
		logoUrl: parts.logoUrl,
		themeColor: parts.isBranded && parts.themeColor ? parts.themeColor : PLATFORM_THEME_COLOR,
		supportEmail: null,
		supportPhone: null,
	};
}

// A null-resolved (platform) brand, used when no tenant brand is available for a chrome surface.
export const platformResolvedBrand: ResolvedBrand = makeResolvedBrand({
	companyId: null,
	displayName: PLATFORM_DISPLAY_NAME,
	logoUrl: null,
	faviconUrl: null,
	themeColor: null,
	isBranded: false,
});

// The frontend seam that turns the authenticated workspace record into one resolved Brand every
// chrome surface (rail, topbar, favicon, title) reads. The backend resolves the chrome brand
// (`chromeBrand`): for a chain member that is the chain parent's brand and colour, so the shell
// wears the chain. When the payload carries none, fall back to the workspace's own identity so a
// standalone company still themes. `themeColor` is always concrete via makeResolvedBrand.
export function resolveBrandFromCompany(company: CompanyResponse | null): ResolvedBrand | null {
	if (!company) return null;
	if (company.chromeBrand) return company.chromeBrand;
	const whiteLabeling = company.whiteLabeling ?? null;
	return makeResolvedBrand({
		companyId: company.id,
		displayName: whiteLabeling?.customAppName ?? company.name,
		faviconUrl: whiteLabeling?.faviconUrl ?? null,
		logoUrl: whiteLabeling?.logoUrl ?? null,
		themeColor: whiteLabeling?.themeColor ?? null,
		isBranded: companyIsBranded(whiteLabeling),
	});
}

// Maps the minimal public branding payload (PublicBrandDto, served on custom domains before a
// workspace is selected) onto the same ResolvedBrand the chrome reads. The public seam already
// applied the entitlement/ACTIVE gate, so isBranded rides through verbatim.
export function publicBrandToResolved(dto: PublicBrandDto): ResolvedBrand {
	return makeResolvedBrand({
		companyId: null,
		displayName: dto.displayName,
		faviconUrl: dto.favicon,
		logoUrl: dto.logo,
		themeColor: dto.themeColor,
		isBranded: dto.isBranded,
	});
}

// The inverse of publicBrandToResolved: flattens a resolved Brand back onto the minimal public
// payload a public render surface (form/offer preview) consumes. isBranded rides through verbatim,
// so no site re-derives the branded decision from raw fields. A null resolve yields the platform brand.
export function resolvedBrandToPublic(brand: ResolvedBrand | null): PublicBrandDto {
	if (!brand) {
		return {
			displayName: 'Vadio',
			endorserName: null,
			endorserKind: null,
			favicon: null,
			isBranded: false,
			logo: null,
			themeColor: PLATFORM_THEME_COLOR,
		};
	}
	return {
		displayName: brand.displayName,
		endorserName: brand.endorsement?.name ?? null,
		endorserKind: brand.endorsement?.kind ?? null,
		favicon: brand.faviconUrl,
		isBranded: brand.isBranded,
		logo: brand.logoUrl,
		themeColor: brand.themeColor,
	};
}

// The rail/topbar/favicon want a square mark; ResolvedBrand carries no dedicated mark column, so
// the favicon (a square asset by definition) doubles as it, falling back to the wordmark logo.
// Reviewer decision (task 7): reuse faviconUrl as the mark, no new column.
export function brandMarkUrl(brand: ResolvedBrand): string | null {
	return brand.faviconUrl ?? brand.logoUrl;
}
